/**
 * useSubscriptionAccess Hook
 * Validates subscription access for organizations
 */

import {useState, useEffect} from 'react';
import {getSubscription, Subscription} from '../services/SubscriptionService';

interface UseSubscriptionAccessResult {
  subscription: Subscription | null;
  hasAccess: boolean;
  isLoading: boolean;
  error: Error | null;
  isTrial: boolean;
  isExpired: boolean;
  daysRemaining: number | null;
}

export const useSubscriptionAccess = (
  organizationId: string | null | undefined,
): UseSubscriptionAccessResult => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setIsLoading(false);
      return;
    }

    const loadSubscription = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const sub = await getSubscription(organizationId);
        setSubscription(sub);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load subscription'));
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscription();
  }, [organizationId]);

  const hasAccess =
    subscription?.status === 'active' ||
    subscription?.status === 'trial' ||
    false;

  const isTrial = subscription?.status === 'trial';
  const isExpired = subscription?.status === 'expired' || subscription?.status === 'cancelled';

  const daysRemaining = subscription?.trialEndDate
    ? Math.ceil(
        (new Date(subscription.trialEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      )
    : null;

  return {
    subscription,
    hasAccess,
    isLoading,
    error,
    isTrial,
    isExpired,
    daysRemaining,
  };
};

